import { useState } from 'react'
import { post } from '../../../../../utils/Api'
import { useGlobalContext } from '../../../../../utils/Context'
import FormLoader from '../../../../../components/loader/FormLoader'
import { Icon } from '@iconify/react'
import { WeightDiscrepancyCheckResult } from '../types'

interface Props {
  onClose: () => void
  onSuccess: () => void
  prefillData?: {
    trackingId: string
    newWeight: number
    amount: number
  }
}

function RecordDiscrepancyModal({ onClose, onSuccess, prefillData }: Props) {
  const { notify } = useGlobalContext()
  const [loading, setLoading] = useState(false)
  const [checkLoading, setCheckLoading] = useState(false)
  const [formData, setFormData] = useState({
    trackingId: prefillData?.trackingId || '',
    newWeight: prefillData?.newWeight?.toString() || '',
    amount: prefillData?.amount?.toString() || '',
    type: 'manual',
    description: '',
  })
  const [checkResult, setCheckResult] =
    useState<WeightDiscrepancyCheckResult | null>(null)

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleCheck = async () => {
    if (!formData.trackingId.trim()) {
      notify('warn', 'Please enter a tracking ID')
      return
    }
    if (!formData.newWeight || parseFloat(formData.newWeight) <= 0) {
      notify('warn', 'Please enter a valid weight')
      return
    }

    setCheckLoading(true)
    setCheckResult(null)

    try {
      const response = await post(
        'admin/weight-discrepancy/check',
        {
          trackingId: formData.trackingId.trim(),
          newWeight: parseFloat(formData.newWeight),
        },
        null,
      )

      if (response.status === 200) {
        setCheckResult(response.data)
        // Auto-fill the suggested amount if available
        if (response.data.suggestedAmount) {
          setFormData((prev) => ({
            ...prev,
            amount: response.data.suggestedAmount.toString(),
          }))
        }
        notify('success', 'Price calculated')
      } else {
        notify('error', response.message || 'Failed to check discrepancy')
      }
    } catch (error) {
      notify('error', 'Failed to check discrepancy')
    } finally {
      setCheckLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!formData.trackingId.trim()) {
      notify('warn', 'Please enter a tracking ID')
      return
    }
    if (!formData.newWeight || parseFloat(formData.newWeight) <= 0) {
      notify('warn', 'Please enter a valid weight')
      return
    }
    if (!formData.amount) {
      notify('warn', 'Please enter a valid amount')
      return
    }

    setLoading(true)

    try {
      const response = await post(
        'admin/weight-discrepancy/record',
        {
          trackingId: formData.trackingId.trim(),
          newWeight: parseFloat(formData.newWeight),
          amount: parseFloat(formData.amount),
          type: formData.type,
          description: formData.description.trim() || undefined,
        },
        null,
      )

      if (response.status === 200) {
        notify('success', 'Discrepancy recorded successfully')
        onSuccess()
        onClose()
      } else {
        notify('error', response.message || 'Failed to record discrepancy')
      }
    } catch (error) {
      notify('error', 'Failed to record discrepancy')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount)
  }

  return (
    <div className='modal-overlay'>
      <div className='modal-content'>
        <div className='modal-header'>
          <h2>Record Weight Discrepancy</h2>
          <button className='close-btn' onClick={onClose}>
            <Icon icon='mdi:close' />
          </button>
        </div>

        <div className='modal-body'>
          <div className='form-group'>
            <label>Tracking ID *</label>
            <input
              type='text'
              name='trackingId'
              value={formData.trackingId}
              onChange={handleChange}
              placeholder='Enter tracking ID (e.g., QC123456789)'
            />
          </div>

          <div className='form-group'>
            <label>New Weight (kg) *</label>
            <div className='flex' style={{ gap: '10px' }}>
              <input
                type='number'
                name='newWeight'
                value={formData.newWeight}
                onChange={handleChange}
                placeholder='Enter the actual weight'
                step='0.1'
                min='0'
                style={{ flex: 1 }}
              />
              <button
                className='btn-primary'
                onClick={handleCheck}
                disabled={checkLoading}
                style={{ padding: '10px 15px', whiteSpace: 'nowrap' }}
              >
                {checkLoading ? 'Checking...' : 'Calculate Price'}
              </button>
            </div>
          </div>

          {checkResult && (
            <div className='check-result'>
              <h4>Price Calculation</h4>
              <div className='result-row'>
                <span>Old Weight</span>
                <span>{checkResult.oldWeight} kg</span>
              </div>
              <div className='result-row'>
                <span>New Weight</span>
                <span>{checkResult.newWeight} kg</span>
              </div>
              <div className='result-row'>
                <span>Old Price</span>
                <span>{formatCurrency(checkResult.oldPrice)}</span>
              </div>
              {checkResult.newPrice && (
                <div className='result-row'>
                  <span>New Price</span>
                  <span>{formatCurrency(checkResult.newPrice)}</span>
                </div>
              )}
              {checkResult.error && (
                <div className='error-message'>
                  <Icon
                    icon='mdi:alert-circle'
                    style={{ marginRight: '8px' }}
                  />
                  {checkResult.message || checkResult.error}
                </div>
              )}
            </div>
          )}

          <div className='form-group'>
            <label>Amount (NGN) *</label>
            <input
              type='number'
              name='amount'
              value={formData.amount}
              onChange={handleChange}
              placeholder='Enter the additional charge amount'
              min='0'
            />
            <p className='helper-text'>
              This is the additional amount to charge for the weight difference
            </p>
          </div>

          {/* <div className='form-group'>
            <label>Type</label>
            <select name='type' value={formData.type} onChange={handleChange}>
              <option value='manual'>Manual</option>
              <option value='automatic'>Automatic</option>
            </select>
          </div> */}

          <div className='form-group'>
            <label>Description (Optional)</label>
            <textarea
              name='description'
              value={formData.description}
              onChange={handleChange}
              placeholder='Add a note about this discrepancy (e.g., "Weight increased from 3kg to 5.5kg as reported by DHL")'
            />
          </div>

          <div className='modal-actions'>
            <button className='btn-secondary' onClick={onClose}>
              Cancel
            </button>
            {loading ? (
              <FormLoader position='center' />
            ) : (
              <button className='btn-success' onClick={handleSubmit}>
                Record Discrepancy
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default RecordDiscrepancyModal
